// (C) Copyright 2020 CNCC Services <https://www.cncc.fr/>
// This program is the sole property of CNCC Services and is not free software.

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Col,
  Divider,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Row,
  Spin
} from "antd";
import {
  CheckOutlined,
  CopyOutlined,
  LoadingOutlined,
  TeamOutlined
} from "@ant-design/icons";
import {
  SurveyDatesWriteRequestDTO,
  SurveyInfoResponseDTO,
  SurveyWriteRequestDTO
} from "../../../../api/backend-openapi";
import { formItemBlockLayout } from "../../../../utils/Constant";
import { navigate } from "@reach/router";
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  EditOutlined,
  QuestionCircleOutlined,
  SaveOutlined,
  SendOutlined,
  StopOutlined
} from "@ant-design/icons/lib";
import DateFormat from "../../../../utils/DateFormat";
import i18n from "i18next";
import { showConfirm } from "../../../components/ConfirmModal";
import { ValidatorResult } from "../../../types/Types";
import moment from "moment";
import {
  Answer,
  Field,
  Question,
  SurveyEditFormProps,
  SurveyWorkflowStatus
} from "../../../types/SurveyTypes";
import {
  requestDatesUpdateSurvey,
  requestDeleteSurvey,
  requestDuplicateSurvey,
  requestGetSurvey,
  requestUpdateSurvey,
  requestWorkflowSurvey
} from "../../../../state/survey/SurveysEffects";
import {
  deleteSurvey,
  updateSurvey
} from "../../../../state/survey/SurveysEvents";
import { validate } from "../../../../validator/SurveyValidator";
import SurveySettingsFormSection from "./SurveySettingsFormSection";
import SurveyDateFormSection from "./SurveyDateFormSection";
import { QuestionBlocks } from "../block/QuestionBlocks";
import { SurveyRefusedPublicationButton } from "./SurveyRefusedPublicationButton";
import {
  canBeDeleted,
  getStatusSurveyFrom
} from "../../../../utils/SurveyUtils";
import SurveyResultsButton from "./results/SurveyResultsButton";
import shortid from "shortid";
import SurveyStatusTag from "./SurveyStatusTag";
import { displayError } from "../../../../utils/displayError";
import SurveyExcelResultsButton from "./results/SurveyExcelResultsButton";
import SurveyPreviewButton from "./preview/SurveyPreviewButton";

const { confirm } = Modal;

export interface SurveyWorkflowStatusLayout {
  label: string;
  color: string;
}

const SurveyForm: React.FunctionComponent<SurveyEditFormProps> = (
  props: SurveyEditFormProps
) => {
  const {
    surveyId,
    editMode,
    setEditMode,
    canEdit,
    canDelete,
    canValidate
  } = props;

  const { t } = useTranslation();

  const [form] = Form.useForm();

  const [
    surveyDetails,
    setSurveyDetails
  ] = useState<SurveyInfoResponseDTO | null>(null);

  const [getResponseReceived, setGetResponseReceived] = useState<boolean>(
    false
  );

  const [questions, setQuestions] = useState<Question[]>([]);

  const [fields, setFields] = useState<Field[]>([]);

  const [buttonLoading, setButtonLoading] = useState<boolean>(false);

  const [sendPushValueToDisplay, setSendPushValueToDisplay] = useState(true);

  useEffect(() => {
    setSendPushValueToDisplay(surveyDetails?.sendPush || false);
  }, [surveyDetails]);

  // Used as keys for react
  const generateIds = (survey: SurveyInfoResponseDTO): void => {
    setQuestions(
      survey.questions.map(
        (question: Question): Question => {
          const { id: questionId, answerChoice, ...rest } = question;
          return {
            ...rest,
            id: questionId || shortid.generate(),
            answerChoice: answerChoice.map(
              (answer: Answer): Answer => {
                const { id: answerId, ...rest } = answer;
                return {
                  ...rest,
                  id: answerId || shortid.generate()
                };
              }
            )
          };
        }
      )
    );
  };

  const resetFormFields = (data: SurveyInfoResponseDTO) => {
    setFields([
      {
        name: ["id"],
        value: data.id
      },
      {
        name: ["title"],
        value: data.title
      },
      {
        name: ["creationDate"],
        value: DateFormat.standardDate(data.creationDate)
      },
      {
        name: ["modificationDate"],
        value: DateFormat.standardDate(data.modificationDate)
      },
      {
        name: ["questions"],
        value: data.questions
      },
      {
        name: ["validatorReadingNote"],
        value: data.validatorReadingNote
      },
      {
        name: ["branch"],
        value: data.branch
      },
      {
        name: ["audience"],
        value: data.audience
      },
      {
        name: ["broadcastListId"],
        value: data?.broadcastListId || ""
      }
    ]);
    setSendPushValueToDisplay(data.sendPush || false);
  };

  useEffect(() => {
    if (surveyId) {
      requestGetSurvey(surveyId);
    }
  }, [surveyId]);

  useEffect(() => {
    return requestGetSurvey.done.watch(({ result }) => {
      setGetResponseReceived(true);
      if (result.ok) {
        if (result.data) {
          setSurveyDetails(result.data);
          generateIds(result.data);
          resetFormFields(result.data);
        } else {
          displayError(result.errorMessage || "Error!", false);
        }
      } else {
        displayError(result.errorMessage || "Error!", false);
      }
    });
  });

  useEffect(() => {
    return requestDuplicateSurvey.done.watch(({ result }) => {
      setButtonLoading(false);
      if (result.ok && result.data) {
        navigate(`/surveys/${result.data.id}`);
        message.success(t("status_survey.duplicate.success"));
      } else {
        displayError(result.errorMessage || "Error!", false);
      }
    });
  });

  useEffect(() => {
    return requestDeleteSurvey.done.watch(({ params, result }) => {
      if (result.ok) {
        deleteSurvey(params.id);
        message.success(t("survey.global_buttons.delete.success"));
        navigate("/surveys");
      } else {
        displayError("survey.global_buttons.delete.error");
      }
    });
  });

  useEffect(() => {
    return requestUpdateSurvey.done.watch(({ result }) => {
      setButtonLoading(false);
      if (result.ok && result.data) {
        setEditMode(false);
        updateSurvey(result.data);
        setSurveyDetails(result.data);
        message.success(t("edit_survey.success"));
      } else {
        displayError(result.errorMessage || "Error!", false);
      }
    });
  });

  useEffect(() => {
    return requestDatesUpdateSurvey.done.watch(({ result }) => {
      setButtonLoading(false);
      if (result.ok && result.data) {
        setEditMode(false);
        updateSurvey(result.data);
        setSurveyDetails(result.data);
        message.success(t("edit_survey.success"));
      } else {
        displayError(result.errorMessage || "Error!", false);
      }
    });
  });

  useEffect(() => {
    return requestWorkflowSurvey.done.watch(({ result }) => {
      setButtonLoading(false);
      if (result.ok && result.data) {
        setEditMode(false);
        updateSurvey(result.data);
        setSurveyDetails(result.data);
        message.success(t("edit_survey.success"));
      } else {
        displayError(result.errorMessage || "Error!", false);
      }
    });
  });

  const spinIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

  const confirmationPopup = async () => {
    return new Promise<boolean>(resolve => {
      confirm({
        title: t("edit_survey.modal.title"),
        content: t("edit_survey.modal.description"),
        okText: t("edit_survey.modal.yes"),
        cancelText: t("edit_survey.modal.no"),
        centered: true,
        onOk() {
          resolve(true);
        },
        onCancel() {
          resolve(false);
        }
      });
    });
  };

  const setValuesAndRequestUpdateSurvey = (
    values: any,
    status: SurveyWorkflowStatus
  ) => {
    questions.forEach(question => {
      // TODO déterminer si on veut utiliser les ID de question générés sur le front ou écraser avec le back
      delete question.id;
      delete question.templateId;
      // TODO déterminer si on veut utiliser les ID de question générés sur le front ou écraser avec le back
      question.answerChoice.forEach(answer => delete answer.id);
    });

    const surveyToUpate: SurveyWriteRequestDTO = {
      title: values.title,
      scheduledAvailabilityDate: values.scheduledAvailabilityDate,
      scheduledAvailabilityEndDate: values.scheduledAvailabilityEndDate,
      scheduledVoteCloseDate: values.scheduledVoteCloseDate,
      scheduledResultAvailabilityDate: values.scheduledResultAvailabilityDate,
      questions: questions,
      type: values.type,
      status,
      branch: values.branch,
      sendPush: sendPushValueToDisplay,
      audience: values.audience,
      broadcastListId: values.broadcastListId
    };

    requestUpdateSurvey({
      id: surveyId,
      dto: surveyToUpate
    });
  };

  const setDatesAndRequestUpdateSurvey = (values: any) => {
    const dateToUpate: SurveyDatesWriteRequestDTO = {
      scheduledAvailabilityDate: values.scheduledAvailabilityDate,
      scheduledAvailabilityEndDate: values.scheduledAvailabilityEndDate,
      scheduledVoteCloseDate: values.scheduledVoteCloseDate,
      scheduledResultAvailabilityDate: values.scheduledResultAvailabilityDate
    };

    requestDatesUpdateSurvey({
      id: surveyId,
      dto: dateToUpate
    });
  };

  const handleSubmit = (values: any, status: SurveyWorkflowStatus) => {
    const validationResult: ValidatorResult = validate(questions);
    if (!validationResult.validate) {
      displayError(validationResult.message);
    } else {
      confirmationPopup().then((confirmed: boolean) => {
        if (confirmed) {
          setValuesAndRequestUpdateSurvey(values, status);
          setButtonLoading(true);
        }
      });
    }
  };

  const handleSubmitDate = (values: any) => {
    const validationResult: ValidatorResult = validate(questions);
    if (!validationResult.validate) {
      displayError(validationResult.message);
    } else {
      confirmationPopup().then((confirmed: boolean) => {
        if (confirmed) {
          setDatesAndRequestUpdateSurvey(values);
          setButtonLoading(true);
        }
      });
    }
  };

  const handleSave = (values: any) => {
    surveyDetails?.publicationDate === null
      ? handleSubmit(values, SurveyWorkflowStatus.SAVED)
      : handleSubmitDate(values);
  };

  const handleSubmitToValidation = () => {
    form
      .validateFields()
      .then(values => {
        handleSubmit(values, SurveyWorkflowStatus.VALIDATION);
      })
      .catch(() => {
        displayError("global.on_finish_failed");
      });
  };

  const onFinishFailed = ({ errorFields }: any) => {
    displayError("global.on_finish_failed");
    form.scrollToField(errorFields[0].name);
  };

  return (
    <>
      {!getResponseReceived ? (
        <div style={{ textAlign: "center" }}>
          <Spin indicator={spinIcon} />
        </div>
      ) : surveyDetails ? (
        <Form
          form={form}
          {...formItemBlockLayout}
          onFinish={handleSave}
          onFinishFailed={onFinishFailed}
          fields={fields}
          initialValues={{
            scheduledAvailabilityDate:
              surveyDetails && surveyDetails.scheduledAvailabilityDate
                ? moment(surveyDetails.scheduledAvailabilityDate)
                : undefined
          }}
        >
          <div className="form-first-item-sticky-top-container">
            <div className="form-first-item-sticky-top-content">
              <div className="my-3">
                <SurveyStatusTag status={getStatusSurveyFrom(surveyDetails)} />
              </div>
              <Divider orientation="left">
                <h3 className="text-secondary mb-0">
                  {t("survey.survey_actions")}
                </h3>
              </Divider>
              <div className="d-flex align-items-center justify-content-between flex-wrap px-3">
                <div className="my-3">
                  <div className="d-flex align-items-center justify-content-center flex-wrap">
                    {!editMode && (
                      <Button
                        htmlType="reset"
                        className="m-2"
                        onClick={() => navigate("/surveys")}
                      >
                        <ArrowLeftOutlined /> {t("edit_survey.return")}
                      </Button>
                    )}
                    <SurveyPreviewButton survey={surveyDetails} />
                    {surveyDetails.publicationDate !== null && (
                      <>
                        <SurveyResultsButton surveyDetails={surveyDetails} />
                        <SurveyExcelResultsButton
                          surveyDetails={surveyDetails}
                        />
                      </>
                    )}
                  </div>
                </div>
                <div>
                  {editMode ? (
                    <>
                      <div className="text-center m-2">
                        <Popconfirm
                          title={t("global.cancel_confirmation")}
                          okText={t("global.yes")}
                          cancelText={t("global.no")}
                          onConfirm={() => {
                            setEditMode(false);
                            form.resetFields();
                            resetFormFields(surveyDetails);
                            setQuestions(surveyDetails.questions);
                          }}
                          placement="top"
                          icon={<QuestionCircleOutlined />}
                          className="m-2"
                        >
                          <Button htmlType="reset">
                            <StopOutlined /> {t("edit_survey.cancel")}
                          </Button>
                        </Popconfirm>
                        <Button
                          className="btn-primary m-2"
                          htmlType="submit"
                          loading={buttonLoading}
                        >
                          <SaveOutlined /> {t("edit_survey.save")}
                        </Button>
                        {surveyDetails.publicationDate === null && (
                          <Button
                            className="btn-success m-2"
                            onClick={handleSubmitToValidation}
                            loading={buttonLoading}
                          >
                            <TeamOutlined /> {t("edit_survey.validation")}
                          </Button>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="d-flex align-items-center justify-content-center flex-wrap">
                        {canEdit && (
                          <Button
                            htmlType="reset"
                            className="btn-primary m-2"
                            onClick={() => {
                              setEditMode(true);
                              if (
                                surveyDetails.publicationDate === undefined ||
                                surveyDetails.publicationDate === null
                              ) {
                                setSendPushValueToDisplay(true);
                              }
                            }}
                          >
                            <EditOutlined /> {t("edit_survey.button")}
                          </Button>
                        )}
                        {surveyDetails.status === SurveyWorkflowStatus.SAVED &&
                          canEdit && (
                            <Button
                              className="btn-success m-2"
                              onClick={() =>
                                showConfirm(
                                  i18n.t("survey.confirmation.validation", {
                                    survey: surveyDetails.title
                                  }),
                                  () => {
                                    requestWorkflowSurvey({
                                      id: surveyDetails.id,
                                      dto: {
                                        status: SurveyWorkflowStatus.VALIDATION
                                      }
                                    });
                                  }
                                )
                              }
                            >
                              <TeamOutlined /> {t("edit_survey.validation")}
                            </Button>
                          )}
                        {surveyDetails.status ===
                          SurveyWorkflowStatus.VALIDATION &&
                          canValidate && (
                            <>
                              <SurveyRefusedPublicationButton
                                surveyId={surveyDetails.id}
                              />
                              <Button
                                className="btn-success m-2"
                                onClick={() =>
                                  showConfirm(
                                    i18n.t("survey.confirmation.validate", {
                                      survey: surveyDetails.title
                                    }),
                                    () => {
                                      requestWorkflowSurvey({
                                        id: surveyDetails.id,
                                        dto: {
                                          status: SurveyWorkflowStatus.VALIDATED
                                        }
                                      });
                                    }
                                  )
                                }
                              >
                                <CheckOutlined /> {t("edit_survey.validate")}
                              </Button>
                            </>
                          )}
                        {surveyDetails.status ===
                          SurveyWorkflowStatus.VALIDATED &&
                          canValidate && (
                            <Button
                              className="btn-success m-2"
                              onClick={() =>
                                showConfirm(
                                  i18n.t("survey.confirmation.publish", {
                                    survey: surveyDetails.title
                                  }),
                                  () => {
                                    requestWorkflowSurvey({
                                      id: surveyDetails.id,
                                      dto: {
                                        status: SurveyWorkflowStatus.PUBLISHED
                                      }
                                    });
                                  }
                                )
                              }
                            >
                              <SendOutlined /> {t("edit_survey.publish")}
                            </Button>
                          )}
                        {surveyDetails.status ===
                          SurveyWorkflowStatus.PUBLISHED &&
                          canValidate && (
                            <Button
                              className="btn-secondary m-2"
                              onClick={() =>
                                showConfirm(
                                  i18n.t("survey.confirmation.unpublish", {
                                    survey: surveyDetails.title
                                  }),
                                  () => {
                                    requestWorkflowSurvey({
                                      id: surveyDetails.id,
                                      dto: {
                                        status: SurveyWorkflowStatus.VALIDATED
                                      }
                                    });
                                  }
                                )
                              }
                            >
                              <StopOutlined /> {t("edit_survey.unpublish")}
                            </Button>
                          )}
                        <Button
                          className="btn-primary m-2"
                          onClick={() =>
                            showConfirm(
                              i18n.t("survey.confirmation.duplicate", {
                                survey: surveyDetails.title
                              }),
                              () => {
                                requestDuplicateSurvey(surveyDetails);
                              }
                            )
                          }
                        >
                          <CopyOutlined /> {t("edit_survey.duplicate")}
                        </Button>
                        {canBeDeleted(surveyDetails) && canDelete && (
                          <Button
                            className="btn-danger m-2"
                            onClick={() =>
                              showConfirm(
                                i18n.t("survey.confirmation.delete", {
                                  survey: surveyDetails.title
                                }),
                                () => {
                                  requestDeleteSurvey(surveyDetails);
                                }
                              )
                            }
                          >
                            <DeleteOutlined /> {t("edit_survey.delete")}
                          </Button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
              {surveyDetails.status === SurveyWorkflowStatus.REFUSED && (
                <Row>
                  <Col xs={24}>
                    <Form.Item
                      name="validatorReadingNote"
                      label={
                        <span>
                          {t("edit_survey.validator_reading_note.text")}
                        </span>
                      }
                    >
                      <Input.TextArea
                        id="validatorReadingNote"
                        readOnly
                        className="readonly-textarea"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              )}
            </div>
          </div>
          <Divider orientation="left">
            <h3 className="text-secondary mb-0">
              {t("survey.responses_parameters")}
            </h3>
          </Divider>
          <SurveyDateFormSection
            survey={surveyDetails}
            editMode={editMode}
            showRequired={editMode}
          />
          <Divider orientation="left">
            <h3 className="text-secondary mb-0">{t("survey.settings")}</h3>
          </Divider>
          <SurveySettingsFormSection
            survey={surveyDetails}
            editMode={surveyDetails.publicationDate === null ? editMode : false}
            showRequired={editMode}
            sendPushValueToDisplay={sendPushValueToDisplay}
            setSendPushValueToDisplay={setSendPushValueToDisplay}
          />
          {(questions.length || editMode) && (
            <>
              <QuestionBlocks
                editMode={
                  surveyDetails.publicationDate === null ? editMode : false
                }
                questions={questions}
                setQuestions={setQuestions}
              />
            </>
          )}
        </Form>
      ) : (
        <div>{t("survey.not_found")}</div>
      )}
    </>
  );
};

export default SurveyForm;
